import React from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  Select,
  Switch,
} from "@chakra-ui/react";
import { modelOptions } from "utils/helperFuncUtils";
import { FiTrash2 } from "react-icons/fi";
import { DarwinAgent, PromptProto } from "build-ts/darwin_agent";
import { DarwinHeader } from "build-ts/common";
import { epochConverterWithTimeZone } from "utils/dateUtils";

interface EvaRightPanelProps {
  agent: DarwinAgent;
  onPromptEditorChange: (value: string) => void;
  onPromptConfigChange?: (field: string, value: any) => void;
  isCreateMode?: boolean;
  defaultTimeZone: string;
  temperatureError: string;
  isPromptEditing: boolean;
  assignedPrompts: DarwinHeader[];
  selectedPromptId: string;
  currentPrompt: PromptProto | undefined;
  isLoadingPrompts: boolean;
  isUpdating: boolean;
  // Event handlers
  handlePromptSelection: (promptId: string) => void;
  handleUpdatePrompt: () => void;
  handleOpenValidateModal: () => void;
  handleGeneratePrompt: () => void;
  setIsPromptEditing: (value: boolean) => void;
  setCurrentPrompt: React.Dispatch<React.SetStateAction<PromptProto | undefined>>;
  setTemperatureError: (value: string) => void;
  setShowDeleteConfirmation: (value: boolean) => void;
  setShowDuplicateConfirmation: (value: boolean) => void;
}

const EvaRightPanel: React.FC<EvaRightPanelProps> = React.memo(({
  agent,
  onPromptEditorChange,
  onPromptConfigChange,
  isCreateMode = false,
  defaultTimeZone,
  temperatureError,
  isPromptEditing,
  assignedPrompts,
  selectedPromptId,
  currentPrompt,
  isLoadingPrompts,
  isUpdating,
  // Event handlers
  handlePromptSelection,
  handleUpdatePrompt,
  handleOpenValidateModal,
  handleGeneratePrompt,
  setIsPromptEditing,
  setCurrentPrompt,
  setTemperatureError,
  setShowDeleteConfirmation,
  setShowDuplicateConfirmation,
}) => {
  // Early return if currentPrompt is undefined and not in create mode
  if (!currentPrompt && !isCreateMode) {
    return (
      <Box className="eva-right-panel eva-small-scrollbar">
        <Box className="eva-prompt">
          <Box className="eva-prompt-content">
            <Text>No prompt selected. Please select a prompt from the dropdown.</Text>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className="eva-right-panel eva-small-scrollbar">
      <Box className="eva-prompt">
        <Box className="eva-prompt-content">
          {/* Prompt Management Header - Only show in edit mode, not create mode */}
          {!isCreateMode && (
            <VStack spacing={4} align="stretch" w="100%">
              <HStack spacing={4} align="center" justify="space-between" w="100%">
                <FormControl maxW="250px">
                  <FormLabel fontSize="sm" mb={2} fontWeight="600">Select Prompt</FormLabel>
                  <Select
                    value={selectedPromptId}
                    onChange={(e) => handlePromptSelection(e.target.value)}
                    size="sm"
                    isDisabled={isLoadingPrompts || isUpdating}
                    placeholder={isLoadingPrompts ? "Loading prompts..." : "Select a prompt"}
                    bg="white"
                    borderColor="gray.300"
                  >
                    {assignedPrompts.map((prompt, index) => {
                      // The API returns only prompt header information
                      const promptId = prompt.id || "";
                      const isActive = prompt.is_active || false;
                      const modifiedDate = prompt.last_modified_date || 0;
                      const version = prompt.version || 1;

                      return (
                        <option key={promptId || `prompt-${index}`} value={String(promptId)}>
                          v{version}_{epochConverterWithTimeZone(modifiedDate || 0, "dd-MMM-yyyy", defaultTimeZone)} {isActive ? "🟢" : ""}
                        </option>
                      );
                    })}
                  </Select>
                </FormControl>

                <HStack spacing={2}>
                  {!isPromptEditing ? (
                    <>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => setIsPromptEditing(true)}
                        isDisabled={!currentPrompt}
                      >
                        Edit Prompt
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="purple"
                        variant="outline"
                        onClick={() => setShowDuplicateConfirmation(true)}
                        isDisabled={!currentPrompt}
                      >
                        Duplicate
                      </Button>
                      <IconButton
                        aria-label="Delete prompt"
                        icon={<FiTrash2 />}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => setShowDeleteConfirmation(true)}
                        isDisabled={!currentPrompt}
                      />
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsPromptEditing(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="green"
                        onClick={() => handleUpdatePrompt()}
                        isLoading={isUpdating}
                      >
                        Save Prompt
                      </Button>
                    </>
                  )}
                </HStack>
              </HStack>
            </VStack>
          )}

          {/* Model Parameters and Description - Side by Side with 3:1 ratio */}
          <HStack spacing={6} align="flex-start" w="100%">
            {/* Description Section - 3/4 width */}
            <Box flex="3">
              <Text fontSize="sm" color="gray.700" fontWeight="600" mb={2}>
                Prompt Description
              </Text>
              <Textarea
              name="prompt description"
              value={currentPrompt?.header?.description || ""}
              onChange={(e) => {
                const value = e.target.value;
                setCurrentPrompt(prev => {
                  if (!prev) return prev;
                  return {
                    ...prev,
                    header: {
                      ...prev.header,
                      description: value
                    }
                  };
                });
                if (onPromptConfigChange) {
                  onPromptConfigChange("header.description", value);
                }
              }}
                placeholder="Prompt description"
                rows={5}
                isDisabled={!isPromptEditing && !isCreateMode}
                borderColor="gray.200"
                fontSize="sm"
              />
            </Box>

            {/* Model Parameters - 1/4 width */}
            <VStack spacing={4} align="stretch" flex="1">
              <FormControl>
                <FormLabel fontSize="sm" mb={2} fontWeight="600">Model</FormLabel>
                <Select
                  value={`${currentPrompt?.model_config?.model_provider}:${currentPrompt?.model_config?.model_name}`}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    const [provider, modelName] = selectedValue.split(":");

                    // Update form state for Darwin proto
                    setCurrentPrompt(prev => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        model_config: {
                          ...prev.model_config,
                          model_name: modelName,
                          model_provider: provider
                        }
                      };
                    });

                    // Update prompt config for parent component
                    if (onPromptConfigChange) {
                      onPromptConfigChange("model_config.model_name", modelName);
                      onPromptConfigChange("model_config.model_provider", provider);
                    }
                  }}
                  isDisabled={!isPromptEditing && !isCreateMode}
                  size="sm"
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "blue.400" }}
                  _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
                >
                  {Object.entries(modelOptions).map(([provider, models]) => (
                    <optgroup key={provider} label={provider.replace("_", " ").toUpperCase()}>
                      {models.map((model) => (
                        <option key={`${provider}:${model}`} value={`${provider}:${model}`}>
                          {model}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Select>
              </FormControl>

              <FormControl isInvalid={!!temperatureError}>
                <FormLabel fontSize="sm" mb={2} fontWeight="600">Temperature</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  step="0.1"
                  value={currentPrompt?.model_config?.temperature?.toString() || ""}
                  onChange={(e) => {
                    const inputValue = e.target.value;

                    if (inputValue === "") {
                      setTemperatureError("");
                      setCurrentPrompt(prev => {
                        if (!prev) return prev;
                        return {
                          ...prev,
                          model_config: {
                            ...prev.model_config,
                            temperature: 0
                          }
                        };
                      });
                      if (onPromptConfigChange) {
                        onPromptConfigChange("model_config.temperature", 0);
                      }
                      return;
                    }

                    const numValue = parseFloat(inputValue);
                    if (isNaN(numValue)) {
                      setTemperatureError("Please enter a valid number");
                      return;
                    }

                    // Validate range before updating
                    if (numValue < 0 || numValue > 2) {
                      setTemperatureError("Temperature must be between 0 and 2");
                      return;
                    }

                    // Only update if valid
                    setTemperatureError("");
                    setCurrentPrompt(prev => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        model_config: {
                          ...prev.model_config,
                          temperature: numValue
                        }
                      };
                    });
                    if (onPromptConfigChange) {
                      onPromptConfigChange("model_config.temperature", numValue);
                    }
                  }}
                  onWheel={(e) => {
                    if (!isPromptEditing && !isCreateMode) return; // Don't allow wheel when disabled

                    e.preventDefault();
                    e.stopPropagation();

                    const currentValue = currentPrompt?.model_config?.temperature ?? 0;
                    const delta = e.deltaY > 0 ? -0.1 : 0.1;
                    const newValue = Math.round((currentValue + delta) * 10) / 10;

                    // Clamp the value between 0 and 2
                    const clampedValue = Math.max(0, Math.min(2, newValue));

                    if (clampedValue !== currentValue) {
                      setTemperatureError("");
                      setCurrentPrompt(prev => {
                        if (prev) {
                          return {
                            ...(prev),
                            model_config: {
                              ...(prev).model_config,
                              temperature: clampedValue
                            }
                          };
                        }
                        return prev;
                      });
                      if (onPromptConfigChange) {
                        onPromptConfigChange("model_config.temperature", clampedValue);
                      }
                    }
                  }}
                  placeholder="0"
                  bg="white"
                  borderColor="gray.300"
                  _hover={{ borderColor: "blue.400" }}
                  _focus={{
                    borderColor: "blue.500",
                    boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)"
                  }}
                  fontWeight="500"
                  isDisabled={!isPromptEditing && !isCreateMode}
                  color={!isPromptEditing && !isCreateMode ? "gray.400" : "black"}
                />
              </FormControl>
            </VStack>
          </HStack>

          {temperatureError && (
            <Text fontSize="xs" color="red.500" mb={2}>
              {temperatureError}
            </Text>
          )}

          {/* System Prompt Section */}
          <VStack spacing={4} align="stretch" w="100%">
          {!isCreateMode ? (
            <HStack justify="space-between" align="center">
              <Text fontSize="sm" color="gray.700" fontWeight="600" mb={0}>
                System Prompt
              </Text>
                {(assignedPrompts.filter(
                  (p: DarwinHeader) => p.is_active
                )[0]?.id === currentPrompt?.header?.id) ? (
                  <Box
                    fontSize="sm"
                    color="green"
                    borderColor="green.500"
                    borderWidth="1px"
                    borderRadius="md"
                    p={1}
                  >
                    Active
                  </Box>
                ) : (
                  <FormControl width="fit-content">
                    <FormLabel m={1}>
                      Activate This Prompt
                      <Switch
                        isChecked={currentPrompt?.header?.is_active || false}
                        onChange={(e) => {
                          const isActive = e.target.checked;
                          setCurrentPrompt(prev => {
                            if (!prev) return prev;
                            return {
                              ...prev,
                              header: {
                                ...prev.header,
                                is_active: isActive
                              }
                            };
                          });
                          if (onPromptConfigChange) {
                            onPromptConfigChange("header.is_active", isActive);
                          }
                        }}
                        colorScheme="blue"
                        ml={2}
                        mb={0}
                        isDisabled={!isPromptEditing && !isCreateMode}
                      />
                    </FormLabel>
                  </FormControl>
                )}
              </HStack>
            ) : (
              <Text fontSize="sm" color="gray.700" fontWeight="600" mb={0} display="flex" alignItems="flex-start">
                System Prompt
              </Text>
            )}
            <Textarea
              name="prompt-editor"
              value={currentPrompt?.template || ""}
              onChange={(e) => {
                const value = e.target.value;
                onPromptEditorChange(value);
                setCurrentPrompt(prev => ({ ...prev, template: value }));
                if (onPromptConfigChange) {
                  onPromptConfigChange("template", value);
                }
              }}
              placeholder="Enter your system prompt here. Use Jinja template syntax for variables like {{agent_name}}, {{plan_name}}, {{MKG_INFO}}, etc."
              className="eva-textarea eva-small-scrollbar"
              isDisabled={!isPromptEditing && !isCreateMode}
              minH={isCreateMode ? "550px" : "498px"}
              bg="white"
              borderColor="gray.300"
              _hover={{ borderColor: "blue.400" }}
              _focus={{ borderColor: "blue.500", boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)" }}
              fontSize="sm"
              fontFamily="mono"
              resize="vertical"
            />

            {!isCreateMode ? (
              <HStack justify="space-between" align="center" w="100%">
                <VStack>
                  <Text fontSize="x-small" color="gray.700" mb={0}>
                    Created at: {currentPrompt?.header?.created_date ? epochConverterWithTimeZone(currentPrompt.header?.created_date, "dd-MMM-yyyy hh:mm a", defaultTimeZone) : ""} {defaultTimeZone.toUpperCase()}
                  </Text>
                  <Text fontSize="x-small" color="gray.700" mb={0}>
                    Modified at: {currentPrompt?.header?.last_modified_date ? epochConverterWithTimeZone(currentPrompt.header?.last_modified_date, "dd-MMM-yyyy hh:mm a", defaultTimeZone) : ""} {defaultTimeZone.toUpperCase()}
                  </Text>
                </VStack>

              <HStack justify="flex-end" gap={3} pt={2}>
                {/* <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenValidateModal}
                  borderColor="gray.300"
                  _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                >
                  Validate & Test
                </Button> */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGeneratePrompt}
                  borderColor="gray.300"
                  _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                >
                  Generate Prompt
                </Button>
              </HStack>
            </HStack>
          ): (
            <Flex justify="flex-end" gap={3} w="100%" pt={2}>
              {/* <Button
                variant="outline"
                size="sm"
                onClick={handleOpenValidateModal}
                borderColor="gray.300"
                _hover={{ borderColor: "blue.400", bg: "blue.50" }}
              >
                  Validate & Test
              </Button> */}
            </Flex>
          )}
          </VStack>
        </Box>
      </Box>
    </Box>
  );
});

EvaRightPanel.displayName = "EvaRightPanel";

export default EvaRightPanel;
